import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { displayLanguage } from '@slideforge/host-bridge';
import { ID_ID, stringsFor, type Strings } from './strings.js';

const StringsContext = createContext<Strings>(ID_ID);
const LocaleContext = createContext<string>('id-ID');

export interface I18nProviderProps {
  readonly children: ReactNode;
  /** Overrides host detection. Used by tests and by `?locale=` for pseudo-loc. */
  readonly locale?: string;
}

export function I18nProvider({ children, locale }: I18nProviderProps) {
  const resolved = useMemo(() => locale ?? detectLocale(), [locale]);
  const strings = useMemo(() => stringsFor(resolved), [resolved]);
  return (
    <LocaleContext.Provider value={resolved}>
      <StringsContext.Provider value={strings}>{children}</StringsContext.Provider>
    </LocaleContext.Provider>
  );
}

export function useStrings(): Strings {
  return useContext(StringsContext);
}

export function useLocale(): string {
  return useContext(LocaleContext);
}

/**
 * `?locale=` wins over the host so a pseudo-locale build can be opened in a real
 * PowerPoint without changing the machine's Office language — which is what
 * makes the localisation check something a developer will actually run.
 */
export function detectLocale(search = globalThis.location?.search ?? ''): string {
  const override = new URLSearchParams(search).get('locale');
  if (override) return override;
  return displayLanguage('id-ID');
}
